import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHeart,
  FaSearch,
  FaUserCheck,
  FaShieldAlt,
  FaRing,
  FaGlobe,
  FaBars,
  FaTimes,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const HeroSection = () => {
  const [activeTab, setActiveTab] = useState("woman");
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const heroImages = [
    "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    "https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    "https://images.unsplash.com/photo-1595268683569-4d896aed0946?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  ];

  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate("/register");
  };

  const t = {
    English: {
      successStories: "Success Stories",
      membership: "Membership",
      help: "Help",
      login: "Login",
      register: "Free Register",
      trustedBadge: "#1 Trusted Matrimony App",
      heroTitle1: "Find Your",
      heroTitleSpecial: "Perfect Match",
      heroTitle2: "Today",
      heroDesc:
        "Join millions of happy couples who found their soulmate on ST MatchZone. We bring people together with love, trust, and tradition.",
      stories: "Success Stories",
      lookingForGirl: "I'm looking for a Bride",
      lookingForBoy: "I'm looking for a Groom",
      age: "Age",
      religion: "Religion",
      motherTongue: "Mother Tongue",
      searchMatches: "Search Matches",
      privacy: "100% Privacy",
      verified: "Verified Profiles",
      bestMatches: "Smart Matching",
    },
    Tamil: {
      successStories: "வெற்றிக் கதைகள்",
      membership: "உறுப்பினர் திட்டம்",
      help: "உதவி",
      login: "உள்நுழைய",
      register: "இலவச பதிவு",
      trustedBadge: "#1 நம்பிக்கையான திருமண சேவை",
      heroTitle1: "உங்கள்",
      heroTitleSpecial: "வாழ்க்கைத் துணையை",
      heroTitle2: "இன்றே தேடுங்கள்",
      heroDesc:
        "ST MatchZone மூலம் தங்கள் வாழ்க்கைத் துணையைத் தேடிக்கொண்ட லட்சக்கணக்கான மகிழ்ச்சியான தம்பதிகளுடன் இணையுங்கள்.",
      stories: "வெற்றிக் கதைகள்",
      lookingForGirl: "பெண் தேடுகிறேன்",
      lookingForBoy: "மாப்பிள்ளை தேடுகிறேன்",
      age: "வயது",
      religion: "மதம்",
      motherTongue: "தாய்மொழி",
      searchMatches: "தேடுங்கள்",
      privacy: "100% தனியுரிமை",
      verified: "சரிபார்க்கப்பட்டவை",
      bestMatches: "சிறந்த பொருத்தங்கள்",
    },
  };

  const txt = t[language];

  return (
    <div className="relative min-h-screen w-full overflow-hidden font-sans bg-gray-900">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img
              src="/logo1.png"
              alt="MatchZone Logo"
              className={`h-14 w-36 rounded-xl shadow-md transition-all ${
                scrolled ? "opacity-100 scale-100" : "opacity-90 scale-95"
              }`}
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-10">
            {["success-stories", "membership", "help"].map((item) => (
              <Link
                key={item}
                to={`/${item}`}
                className={`font-semibold text-sm hover:text-rose-500 transition-all duration-200 ${
                  scrolled ? "text-gray-700" : "text-white/90"
                }`}
              >
                {txt[item.replace("-", "")] || txt[item]}
              </Link>
            ))}

            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 font-semibold text-sm hover:text-rose-500 transition-all duration-200 ${
                scrolled ? "text-gray-700" : "text-white/90"
              }`}
            >
              <FaGlobe /> {language === "English" ? "தமிழ்" : "English"}
            </button>

            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className={`font-bold px-6 py-2 rounded-full border-2 transition-all duration-200 ${
                  scrolled
                    ? "text-rose-600 border-rose-300 hover:bg-rose-50"
                    : "text-white border-white/30 hover:bg-white/10"
                }`}
              >
                {txt.login}
              </Link>
              <Link
                to="/register"
                className="bg-rose-600 text-white px-6 py-2 rounded-full font-bold shadow-xl shadow-rose-600/40 hover:bg-rose-700 hover:scale-105 transition-all duration-200"
              >
                {txt.register}
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden z-50 text-2xl p-3 rounded-full transition-all duration-200 ${
              scrolled || mobileMenuOpen
                ? "bg-white/80 text-rose-600 shadow-md"
                : "bg-white/20 text-white"
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 bg-white z-40 flex flex-col pt-24 px-6 md:hidden h-screen overflow-y-auto"
          >
            <div className="flex flex-col space-y-6 text-center">
              {["success-stories", "membership", "help"].map((item) => (
                <Link
                  key={item}
                  to={`/${item}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xl font-bold text-gray-800 hover:text-rose-600"
                >
                  {txt[item.replace("-", "")] || txt[item]}
                </Link>
              ))}
              <button
                onClick={() => {
                  toggleLanguage();
                  setMobileMenuOpen(false);
                }}
                className="text-xl font-bold text-gray-800 hover:text-rose-600 flex items-center justify-center gap-2"
              >
                <FaGlobe /> {language === "English" ? "தமிழ்" : "English"}
              </button>
              <hr className="border-gray-100 my-4" />
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="text-xl font-bold text-rose-600"
              >
                {txt.login}
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="bg-rose-600 text-white py-4 rounded-xl font-bold shadow-lg shadow-rose-500/30"
              >
                {txt.register}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Slider */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImage}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
            className="absolute inset-0"
          >
            <img
              src={heroImages[currentImage]}
              alt="Couple"
              className="w-full h-full object-cover object-center"
            />
            {/* Gradient Overlay - Improved Visibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30 sm:to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40"></div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-center pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Text */}
          <div className="lg:col-span-7 text-white text-center lg:text-left space-y-6 md:space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm font-medium text-rose-200 mx-auto lg:mx-0"
            >
              <FaUserCheck className="text-rose-400" /> {txt.trustedBadge}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight drop-shadow-lg"
            >
              {txt.heroTitle1} <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-pink-500 to-orange-400">
                {txt.heroTitleSpecial}
              </span>{" "}
              <br />
              {txt.heroTitle2}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-gray-200 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-light"
            >
              {txt.heroDesc}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-6 pt-4"
            >
              <div className="flex -space-x-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-12 h-12 rounded-full border-2 border-white overflow-hidden"
                  >
                    <img
                      src={`https://randomuser.me/api/portraits/women/${
                        i * 12
                      }.jpg`}
                      alt="User"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                <div className="w-12 h-12 rounded-full border-2 border-white bg-rose-600 flex items-center justify-center text-white text-xs font-bold">
                  10k+
                </div>
              </div>
              <div className="text-left">
                <div className="font-bold text-xl">{txt.stories}</div>
                <div className="text-sm text-rose-300 flex items-center gap-1">
                  <FaHeart className="text-xs" /> 10,000+ Happy Couples
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Search Form */}
          <div className="lg:col-span-5 w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="bg-white/95 backdrop-blur-xl p-6 sm:p-8 rounded-3xl shadow-2xl border border-white/50 w-full max-w-md mx-auto lg:ml-auto relative overflow-hidden"
            >
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-rose-100 rounded-full blur-3xl -mr-16 -mt-16 opacity-50"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-100 rounded-full blur-3xl -ml-16 -mb-16 opacity-50"></div>

              <h3 className="text-2xl font-bold text-gray-800 mb-6 relative z-10 text-center">
                Let's find your match
              </h3>

              <div className="flex p-1 bg-gray-100 rounded-xl mb-6 relative z-10">
                <button
                  onClick={() => setActiveTab("woman")}
                  className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    activeTab === "woman"
                      ? "bg-white text-rose-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {activeTab === "woman" && (
                    <FaCheckCircle className="text-xs" />
                  )}{" "}
                  Bride
                </button>
                <button
                  onClick={() => setActiveTab("man")}
                  className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    activeTab === "man"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {activeTab === "man" && <FaCheckCircle className="text-xs" />}{" "}
                  Groom
                </button>
              </div>

              <form onSubmit={handleSearch} className="space-y-4 relative z-10">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide ml-1">
                      {txt.age}
                    </label>
                    <div className="flex items-center gap-2">
                      <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-gray-700 font-medium appearance-none cursor-pointer">
                        {[...Array(41)].map((_, i) => (
                          <option key={i} value={18 + i}>
                            {18 + i}
                          </option>
                        ))}
                      </select>
                      <span className="text-gray-400 font-medium">-</span>
                      <select
                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-gray-700 font-medium appearance-none cursor-pointer"
                        defaultValue={25}
                      >
                        {[...Array(41)].map((_, i) => (
                          <option key={i} value={18 + i}>
                            {18 + i}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide ml-1">
                    {txt.religion}
                  </label>
                  <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-gray-700 font-medium cursor-pointer">
                    <option>Hindu</option>
                    <option>Muslim</option>
                    <option>Christian</option>
                    <option>Jain</option>
                    <option>Sikh</option>
                    <option>Buddhist</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide ml-1">
                    {txt.motherTongue}
                  </label>
                  <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-gray-700 font-medium cursor-pointer">
                    <option>Tamil</option>
                    <option>Malayalam</option>
                    <option>Telugu</option>
                    <option>Kannada</option>
                    <option>Hindi</option>
                    <option>English</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-rose-600 to-pink-600 text-white font-bold rounded-xl shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-2"
                >
                  <FaSearch /> {txt.searchMatches}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Stats Strip - Responsive */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md border-t border-white/10 py-4 hidden md:block z-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-3 gap-8 text-white/90">
            <div className="flex items-center justify-center lg:justify-start gap-4 group cursor-default">
              <div className="p-3 bg-white/10 rounded-full group-hover:bg-rose-500/20 transition-colors">
                <FaShieldAlt className="text-2xl text-rose-400" />
              </div>
              <div>
                <div className="font-bold text-lg">{txt.privacy}</div>
                <div className="text-xs text-white/60 group-hover:text-white/80 transition-colors">
                  100% Secure Data
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center lg:justify-start gap-4 group cursor-default">
              <div className="p-3 bg-white/10 rounded-full group-hover:bg-blue-500/20 transition-colors">
                <FaUserCheck className="text-2xl text-blue-400" />
              </div>
              <div>
                <div className="font-bold text-lg">{txt.verified}</div>
                <div className="text-xs text-white/60 group-hover:text-white/80 transition-colors">
                  Govt ID Verification
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center lg:justify-start gap-4 group cursor-default">
              <div className="p-3 bg-white/10 rounded-full group-hover:bg-yellow-500/20 transition-colors">
                <FaRing className="text-2xl text-yellow-400" />
              </div>
              <div>
                <div className="font-bold text-lg">{txt.bestMatches}</div>
                <div className="text-xs text-white/60 group-hover:text-white/80 transition-colors">
                  AI Powered Matching
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
